/**
 * Competition API Client — Arena Client (WP-339 / WP-5b)
 *
 * Typed `fetch` wrappers for the two competitive-scoring HTTP endpoints:
 *   - `POST /api/competition/scores` — submit a finished match's score by
 *     `matchId` (the server resolves the replay, captures on-demand, verifies,
 *     auto-publishes, and scores — WP-338). The client never sends a
 *     `replayHash`: it cannot compute `computeStateHash`.
 *   - `GET /api/me/scores` — the authenticated player's submitted scores.
 *
 * Layer-boundary contract: imports nothing from `boardgame.io`,
 * `@legendary-arena/game-engine`, `@legendary-arena/registry`,
 * `@legendary-arena/preplan`, `@legendary-arena/vue-sfc-loader`, or any
 * `apps/server/**` module. `MyCompetitiveScore` is declared inline by
 * structural compatibility with the server's `CompetitiveScoreRecord`
 * (`apps/server/src/competition/competition.types.ts`) — the server is
 * authoritative on the shape; the client must not import server-layer types.
 * Mirrors the WP-104 `ownerProfileApi.ts` never-throw Bearer-fetch pattern.
 *
 * Authority: WP-339 §Scope (In) §A; EC-369; D-24126 (the server surfaces).
 */

import { buildApiUrl } from './apiBaseUrl';

/**
 * A submitted competitive score, mirroring the server's
 * `CompetitiveScoreRecord` display fields by structural compatibility (the
 * `accountId` owner field is intentionally absent — a player's own scores are
 * implicitly theirs). Declared locally because the client must not import
 * server-layer types.
 */
export interface MyCompetitiveScore {
  readonly submissionId: number;
  readonly replayHash: string;
  readonly scenarioKey: string;
  readonly rawScore: number;
  readonly finalScore: number;
  readonly parVersion: string;
  readonly scoringConfigVersion: number;
  readonly stateHash: string;
  readonly createdAt: string;
  // why: WP-583 — the server already returns the full component breakdown inside
  // `record.scoreBreakdown` (built + persisted server-side); the endgame screen
  // renders it verbatim. Declared as a local structural shape (mirroring the engine
  // `ScoreBreakdown`) because this file must not import engine/server types.
  // Optional: a record without a breakdown still typechecks and renders the headline.
  readonly scoreBreakdown?: CompetitiveScoreBreakdown;
  // why: WP-593 — the derived per-seat identity roster for the endgame report card
  // ("Player N (Bot)" / "Player N (@handle)"). NOT a field of the server's locked
  // CompetitiveScoreRecord: it is match metadata the submit response returns
  // alongside the record, merged into this shape by `submitCompetitiveScore`.
  // Optional: absent for guests, the GET /api/me/scores history read, and older
  // records — the card then shows plain "Player N".
  readonly seatIdentities?: readonly CompetitiveSeatIdentity[];
}

/**
 * One seat's display identity on the endgame report card (WP-593), a structural
 * mirror of the server's `MatchSeatIdentity` (derived match metadata, never a
 * persisted score-row column). `handle` is `null` for a bot seat, a guest seat,
 * and an account with no display handle — all rendered as a plain "Player N".
 */
export interface CompetitiveSeatIdentity {
  readonly playerId: string;
  readonly isBot: boolean;
  readonly handle: string | null;
}

/**
 * Per-penalty-event counts, keyed by the engine's PenaltyEventType members.
 * Declared locally (no engine import) — mirrors the engine `PenaltyEventType`
 * union; the server is authoritative on the keys.
 */
export interface CompetitivePenaltyCounts {
  readonly villainEscaped: number;
  readonly bystanderLost: number;
  readonly schemeTwistNegative: number;
  readonly mastermindTacticUntaken: number;
  readonly scenarioSpecificPenalty: number;
}

/**
 * One player's contribution to the team-aggregate reward inputs (structural
 * mirror of the engine `PlayerScoringContribution`, WP-588). The competitive
 * score is a shared-team score; this is a display-only split of the same totals.
 */
export interface CompetitivePlayerContribution {
  readonly playerId: string;
  readonly victoryPoints: number;
  readonly bystandersRescued: number;
  // why: WP-621 / D-24432 — per-seat team-contribution counts (the engine
  // populates them since WP-616 / D-24427; the endgame report card surfaces
  // them). Optional: records persisted before WP-616 carry none, and the
  // report card then shows only that seat's VP + bystanders rescued.
  readonly mastermindTacticsDefeated?: number;
  readonly villainsDefeated?: number;
  readonly henchmenDefeated?: number;
  // why: WP-708 / D-24531 — this seat's per-match synergy tally (display-only,
  // mirrors the engine PlayerScoringContribution). Optional: records persisted
  // before WP-708 carry none, and the report card then omits the Synergy line.
  readonly conditionalClausesPlayed?: number;
  readonly conditionalClausesAssembled?: number;
  // why: WP-709 / D-24532 — this seat's per-match Realized Value % inputs (display-only,
  // mirrors the engine PlayerScoringContribution). Optional: records persisted before
  // WP-709 carry none, and the report card then omits the Realized Value % line.
  readonly conditionalClausesPotentialValue?: number;
  readonly conditionalClausesRealizedValue?: number;
}

/**
 * The derived inputs a breakdown was computed from (structural mirror of the
 * engine `ScoringInputs`).
 */
export interface CompetitiveScoringInputs {
  readonly rounds: number;
  readonly victoryPoints: number;
  readonly bystandersRescued: number;
  readonly escapes: number;
  readonly penaltyEventCounts: CompetitivePenaltyCounts;
  // why: WP-588 — per-player split of victoryPoints + bystandersRescued for the
  // endgame report card. Optional: records persisted before WP-588 carry none, and
  // the endgame then shows only the team totals.
  readonly perPlayer?: readonly CompetitivePlayerContribution[];
  // why: WP-591 / D-24400 — whether the match was lost (adds the loss penalty).
  // Optional: absent on rows persisted before WP-591 (treated as a win).
  readonly matchLost?: boolean;
}

/**
 * The scenario baseline a `parScore` was derived from (structural mirror of the
 * engine `ParBaseline`). Lets the endgame screen show PAR's derivation, not just
 * the final PAR value.
 */
export interface CompetitiveParBaseline {
  readonly bystandersPar: number;
  readonly victoryPointsPar: number;
  readonly escapesPar: number;
  // why: WP-591 / D-24400 — PAR now models the expected twists + bystanders-lost too.
  // Optional: rows persisted before WP-591 have a 3-field baseline in their stored
  // breakdown, so the derivation shows those terms only when present.
  readonly schemeTwistsPar?: number;
  readonly bystandersLostPar?: number;
}

/**
 * The full competitive score component breakdown, a local structural mirror of
 * the engine `ScoreBreakdown` (the server returns it verbatim inside a record).
 * The client never recomputes these — it renders them.
 */
export interface CompetitiveScoreBreakdown {
  readonly inputs: CompetitiveScoringInputs;
  // why: WP-585 / D-24394 — no weightedRoundCost; the round-cost term was removed
  // from the scoring formula (the rulebook has no round penalty).
  readonly weightedPenaltyTotal: number;
  readonly penaltyBreakdown: CompetitivePenaltyCounts;
  // why: WP-591 / D-24400 — the flat loss penalty added to rawScore when the match
  // was lost (0 for a win). Optional: absent on rows persisted before WP-591.
  readonly weightedLossPenalty?: number;
  // why: WP-599 / D-24409 — the dedicated bystander reward was removed from the
  // scoring model (rescued bystanders score only as their 1 VP). New records (config
  // v5+) omit this field; it stays optional so older persisted rows (which carry it)
  // still parse. The display no longer reads it.
  readonly weightedBystanderReward?: number;
  readonly weightedVictoryPointReward: number;
  readonly rawScore: number;
  readonly parScore: number;
  // why: WP-587 / D-24396 — the baseline parScore was derived from, for the endgame
  // PAR-derivation display. Optional: rows persisted before WP-587 have no parBaseline
  // in their stored breakdown, so the derivation degrades gracefully to just the PAR
  // value on those older records.
  readonly parBaseline?: CompetitiveParBaseline;
  readonly finalScore: number;
  readonly scoringConfigVersion: number;
}

/**
 * Result of a submit attempt. Never throws: a network failure surfaces as
 * `status: 0`. `record`/`wasExisting` are present only on HTTP 200; `error`
 * carries the server's `{ error: <code> }` string on a non-200.
 */
export interface SubmitScoreResult {
  readonly status: number;
  readonly wasExisting: boolean | null;
  readonly record: MyCompetitiveScore | null;
  readonly error: string | null;
}

/**
 * Result of a my-scores fetch. Never throws (`status: 0` on network failure).
 * `scores` is present only on HTTP 200.
 */
export interface MyScoresResult {
  readonly status: number;
  readonly scores: MyCompetitiveScore[] | null;
  readonly error: string | null;
}

/**
 * Best-effort extraction of the server's `{ error: <code> }` string from a
 * non-200 response body. Returns `null` when the body is absent, not JSON, or
 * carries no string `error`.
 *
 * @param response The non-200 fetch response.
 * @returns The error code string, or `null`.
 */
async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : null;
  } catch {
    // why: a non-200 with no body / malformed JSON is a transport-level failure;
    // surface only the status so the caller can render a generic message.
    return null;
  }
}

/**
 * Submit a finished match's competitive score by `matchId`. The server resolves
 * the replay, captures on-demand if needed, verifies, auto-publishes, and scores
 * (WP-338); the client sends only `matchId` because it cannot compute the
 * `replayHash`. `Authorization: Bearer` is attached only for an authenticated
 * caller; a `null` token yields a request the server rejects with 401 (the
 * caller should not submit for a guest — see `useCompetitiveSubmitOnGameover`).
 *
 * @param authToken The bearer token, or `null` for an unauthenticated caller.
 * @param matchId The finished match's id.
 * @returns The submit result (never throws; `status: 0` on network failure).
 */
export async function submitCompetitiveScore(
  authToken: string | null,
  matchId: string,
): Promise<SubmitScoreResult> {
  let response: Response;
  try {
    response = await fetch(buildApiUrl('/api/competition/scores'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // why: the submit key is matchId, NOT replayHash — the client cannot run
        // computeStateHash; the server resolves the hash from the matchId (WP-338).
        ...(authToken === null ? {} : { Authorization: `Bearer ${authToken}` }),
      },
      body: JSON.stringify({ matchId }),
    });
  } catch {
    return { status: 0, wasExisting: null, record: null, error: null };
  }
  if (response.status === 200) {
    const body = (await response.json()) as {
      record: MyCompetitiveScore;
      wasExisting: boolean;
      // why: WP-593 — the derived per-seat roster the server returns beside the
      // record; merged into the record so the endgame panel reads one shape.
      seatIdentities?: readonly CompetitiveSeatIdentity[];
    };
    return {
      status: 200,
      wasExisting: body.wasExisting,
      // why: WP-593 — fold seatIdentities into the record (the server keeps it a
      // sibling field so the record stays the locked score-row shape). Omitted
      // rather than set to undefined so the exactOptionalPropertyTypes shape holds.
      record:
        body.seatIdentities === undefined
          ? body.record
          : { ...body.record, seatIdentities: body.seatIdentities },
      error: null,
    };
  }
  return {
    status: response.status,
    wasExisting: null,
    record: null,
    error: await readErrorCode(response),
  };
}

/**
 * Fetch the authenticated player's submitted competitive scores (newest first).
 *
 * @param authToken The bearer token, or `null` for an unauthenticated caller.
 * @returns The my-scores result (never throws; `status: 0` on network failure).
 */
export async function fetchMyScores(
  authToken: string | null,
): Promise<MyScoresResult> {
  let response: Response;
  try {
    response = await fetch(buildApiUrl('/api/me/scores'), {
      method: 'GET',
      headers:
        authToken === null ? {} : { Authorization: `Bearer ${authToken}` },
    });
  } catch {
    return { status: 0, scores: null, error: null };
  }
  if (response.status === 200) {
    const body = (await response.json()) as { scores: MyCompetitiveScore[] };
    return { status: 200, scores: body.scores, error: null };
  }
  return {
    status: response.status,
    scores: null,
    error: await readErrorCode(response),
  };
}
